import React, { useEffect, useState } from "react";

import { API, graphqlOperation } from "aws-amplify";
import { searchAccountName } from "../../../graphql/account/account-queries";
import { countInvoices } from "../../../graphql/reloadyard/custom-queries";

import { Box, Grid, InputAdornment, Typography } from "@material-ui/core";

import {
  ArrayField,
  ArrayInput,
  BooleanField,
  Button,
  Datagrid,
  FormDataConsumer,
  Labeled,
  maxLength,
  maxValue,
  minValue,
  number,
  NumberField,
  NumberInput,
  RadioButtonGroupInput,
  required,
  SimpleFormIterator,
  TextField,
  TextInput,
  useNotify,
} from "react-admin";
import { useForm } from "react-final-form";

import { AddressInput } from "../../../components/common/AddressInput";
import {
  DefaultTaxRates,
  DefaultTaxRatesAST,
  PaymentTermsCodes,
  TrueOrFalse,
} from "../../../utils/constants";
import { RemoveIcon } from "../../../components/common/iconography";
import { logError } from "../../../utils/appUtils";
import {
  fetchTriggerEnabled,
  getTriggerEnabled,
} from "../../../utils/zapierUtils";

const validateTaxUnicity = (value, allValues) => {
  const allCodes = allValues.taxRates || [];

  if (value && allCodes) {
    const duplicates = allCodes.filter(
      (item) =>
        item?.taxCode?.toLowerCase().replace(/\s/g, "") ===
        value.toLowerCase().replace(/\s/g, "")
    ).length;

    if (duplicates > 1) {
      // return an object just like the other validators
      return { message: "Duplicate entry" };
    }
  }
  return undefined;
};

const validateTermUnicity = (value, allValues) => {
  const allCodes = allValues.paymentTerms?.items || [];

  if (value && allCodes) {
    const duplicates = allCodes.filter(
      (item) =>
        item?.code?.toLowerCase().replace(/\s/g, "") ===
        value.toLowerCase().replace(/\s/g, "")
    ).length;

    if (duplicates > 1) {
      // return an object just like the other validators
      return { message: "Duplicate entry" };
    }
  }
  return undefined;
};

// QBO realmIds are digits, Xero and Business Central use GUIDs, NetSuite
// sandboxes add _SB1; anything else (spaces, a pasted URL) is a typo
const validateCompanyIdFormat = (value) =>
  value && !/^[A-Za-z0-9_-]+$/.test(value.trim())
    ? "Letters, digits, - and _ only"
    : undefined;

const validateRequired = [required()];
const validateCompanyId = [
  required(),
  maxLength(64),
  validateCompanyIdFormat,
];
const validateTaxCode = [required(), validateTaxUnicity];
const validateTaxRate = [required(), number(), minValue(0), maxValue(100)];
const validateTermCode = [required(), validateTermUnicity];
const validateDueDays = [required(), number(), minValue(0), maxValue(120)];
const validateInvoiceCounter = (baseMin) => (value, allValues, meta) => {
  // meta sat on saveValidate with mutators.setFieldData
  const effectiveMin = meta?.data?.newCounter ?? baseMin ?? 0;

  const validators = [required(), number(), minValue(effectiveMin)];
  for (const v of validators) {
    const err = v(value, allValues, meta);
    if (err) return err;
  }
  return undefined;
};

const InvoicingPanel = ({ disabled, ...props }) => {
  return disabled ? <ShowPanel {...props} /> : <EditPanel {...props} />;
};

const DefaultRateField = ({ record, source, label, taxRates }) => {
  // Only show checkmark for first row
  const rowIndex = taxRates?.findIndex((row) => row.taxCode === record.taxCode);
  return (
    <BooleanField
      source={source}
      label={label}
      record={{ ...record, [source]: rowIndex === 0 }}
    />
  );
};

const ShowPanel = ({ disabled, ...props }) => {
  const { record } = props;

  return (
    <Grid container spacing={1}>
      <Grid item xs={6}>
        <Labeled label="Invoice Number Prefix">
          <TextField source={"invoiceNumberPrefix"} />
        </Labeled>
      </Grid>
      <Grid item xs={6}>
        <Labeled label="Invoice Counter">
          <NumberField source={"invoiceCounter"} />
        </Labeled>
      </Grid>
      {record?.externalCompanyId && (
        <Grid item xs={12}>
          <Labeled label="Accounting Company ID">
            <TextField source={"externalCompanyId"} />
          </Labeled>
        </Grid>
      )}
      {record?.remittanceAddress && (
        <Grid item xs={12}>
          <Labeled label="Remit To Address">
            <div>
              <Typography>{record.remittanceAddress.street}</Typography>
              <Typography>
                {record.remittanceAddress.city},{" "}
                {record.remittanceAddress.state}{" "}
                {record.remittanceAddress.zipCode}
              </Typography>
            </div>
          </Labeled>
        </Grid>
      )}
      <Grid item xs={12} md={6}>
        <Labeled label="TAX RATES" fullWidth>
          <ArrayField source="taxRates">
            <Datagrid>
              <TextField source="taxCode" label="Tax Code" />
              <TextField source="name" label="Name" />
              <TextField source="taxRate" label="Tax Rate" />
              <DefaultRateField
                source="isDefault"
                label="Default Rate"
                taxRates={record.taxRates}
              />
            </Datagrid>
          </ArrayField>
        </Labeled>
      </Grid>
      <Grid item xs={12} md={6}>
        <Labeled label="PAYMENT TERMS" fullWidth>
          <ArrayField source="paymentTerms.items">
            <Datagrid>
              <TextField source="code" label="Code" />
              <TextField source="name" label="Name" />
              <NumberField source="dueDays" label="Due Days" />
            </Datagrid>
          </ArrayField>
        </Labeled>
      </Grid>
    </Grid>
  );
};

const EditPanel = ({ disabled, record, ...props }) => {
  const form = useForm();
  const [astVisible, setAstVisible] = useState(
    getTriggerEnabled("invoiceCreated")
  );
  const [minCounter, setMinCounter] = useState(record?.invoiceCounter ?? 0);

  const fieldState = form.getFieldState("invoiceCounter");
  const effectiveMinCounter = fieldState?.data?.newCounter ?? minCounter;

  useEffect(() => {
    const initializeData = async () => {
      const triggerEnabled = await fetchTriggerEnabled("invoiceCreated");
      setAstVisible(triggerEnabled);

      if (!record?.id) return;

      try {
        const invoiceResponse = await API.graphql(
          graphqlOperation(countInvoices, {
            filter: { reloadYardId: { eq: record.id }, active: { eq: "true" } },
            limit: 1,
          })
        );

        const invoiceCount = invoiceResponse?.data?.searchInvoices?.total || 0;
        if (invoiceCount === 0) {
          // OPTIMIZATION: Only set if different to avoid unnecessary re-render
          if (minCounter !== 0) setMinCounter(0);
        } else {
          // If there are invoices, keep the current minCounter or use record.invoiceCounter
          // Note: The exact minCounter will be validated in ReloadYardEdit.js during saving.
          // OPTIMIZATION: Only set if different to avoid unnecessary re-render (per Adam's feedback)
          const newMinCounter = record?.invoiceCounter ?? 0;
          if (minCounter !== newMinCounter) {
            setMinCounter(newMinCounter);
          }
        }
      } catch (error) {
        logError("reloadYard:InvoicingPanel:calculateMinCounter", error, false);
      }
    };
    initializeData();
  }, [record?.id, record?.invoiceCounter, minCounter]);

  const getTaxRatesHelperText = (source) => {
    const match = source.match(/\[(\d+)\]/);
    const index = match ? parseInt(match[1], 10) : null;
    return index === 0 ? "Default rate" : false;
  };

  const handleAstChange = (value) => {
    if (value === true) form.change("taxRates", DefaultTaxRatesAST);
    else if (record?.taxRates) form.change("taxRates", record.taxRates);
    else form.change("taxRates", DefaultTaxRates);
  };

  return (
    <FormDataConsumer>
      {({ formData, ...rest }) => (
        <Grid container direction="row" spacing={1}>
          <Grid item xs={12} md={4}>
            <TextInput
              source="invoiceNumberPrefix"
              label="Invoice Number Prefix"
              variant="outlined"
              fullWidth
              disabled={disabled}
              inputProps={{
                maxLength: 4,
                style: { textAlign: "right", textTransform: "uppercase" },
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">—</InputAdornment>,
              }}
              helperText={false}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <NumberInput
              source="invoiceCounter"
              label="Invoice Counter"
              variant="outlined"
              fullWidth
              disabled={disabled}
              min={effectiveMinCounter}
              validate={validateInvoiceCounter(effectiveMinCounter)}
              parse={(value) => {
                if (value === "") return undefined;
                const num = Number(value);
                return !isNaN(num) && isFinite(num)
                  ? Math.floor(num)
                  : undefined;
              }}
              helperText={false}
            />
          </Grid>

          <Grid item xs={12} md={10}>
            <Typography gutterBottom variant="overline" color="textSecondary">
              Remit To Address
            </Typography>
            <AddressInput
              source="remittanceAddress"
              variant="outlined"
              fullWidth
              showBusinessName={true}
              parse={(v) => (v !== "" ? v : undefined)}
              allValues={formData}
            />
          </Grid>
          <Grid item xs={12}>
            <fieldset>
              <legend>
                <Typography variant="overline" color="textSecondary">
                  External Mappings
                </Typography>
              </legend>

              <Grid container spacing={1}>
                {astVisible && (
                  <>
                    <Grid item xs={12}>
                      <Grid container>
                        <Grid item xs={12} md={4}>
                          <TextInput
                            source="externalCompanyId"
                            label="Accounting Company ID"
                            variant="outlined"
                            fullWidth
                            disabled={disabled}
                            validate={validateCompanyId}
                            parse={(v) => (v !== "" ? v : null)}
                            helperText="Company file your Zaps sync invoices to, e.g. QuickBooks Online Company ID (realmId)"
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <RadioButtonGroupInput
                        source="ast"
                        label="Auto Sales Tax (AST)"
                        choices={TrueOrFalse}
                        defaultValue={false}
                        onChange={handleAstChange}
                        disabled={disabled}
                        row
                        style={{ marginTop: 12 }}
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12} md={9}>
                  <ArrayInput source="taxRates" label="TAX RATES" fullWidth>
                    <SimpleFormIterator
                      disabled={disabled || formData.ast === true}
                      disableRemove={
                        formData.ast === true ||
                        formData.taxRates?.length === 1 ||
                        disabled
                      }
                      disableAdd={formData.ast === true || disabled}
                      disableReordering={formData.ast === true || disabled}
                    >
                      <FormDataConsumer>
                        {({
                          scopedFormData, // The data for this item of the ArrayInput
                          getSource, // Function to get the valid source inside an ArrayInput. Translates to "inventories.items[].property"
                          ...rest
                        }) => (
                          <Grid container spacing={1}>
                            <Grid item xs={12} md={4}>
                              <TextInput
                                source={getSource("taxCode")}
                                label="Tax Rate Code"
                                validate={validateTaxCode}
                                variant="outlined"
                                fullWidth
                                disabled={disabled || formData.ast === true}
                                helperText={getTaxRatesHelperText(
                                  getSource("")
                                )}
                              />
                            </Grid>
                            <Grid item xs={12} md={5}>
                              <TextInput
                                source={getSource("name")}
                                label="Name"
                                validate={validateRequired}
                                variant="outlined"
                                fullWidth
                                disabled={disabled || formData.ast === true}
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <NumberInput
                                source={getSource("taxRate")}
                                label="Tax Rate"
                                validate={validateTaxRate}
                                placeholder="0.00"
                                variant="outlined"
                                fullWidth
                                disabled={disabled || formData.ast === true}
                                helperText={false}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      {" "}
                                      %{" "}
                                    </InputAdornment>
                                  ),
                                  inputMode: "decimal",
                                  inputProps: {
                                    min: 0,
                                    max: 100,
                                    step: "0.01",
                                    style: { textAlign: "right" },
                                  },
                                }}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                          </Grid>
                        )}
                      </FormDataConsumer>
                    </SimpleFormIterator>
                  </ArrayInput>
                </Grid>
                <Grid item xs={12} md={11}>
                  <ArrayInput
                    source="paymentTerms.items"
                    label="PAYMENT TERMS"
                    fullWidth
                  >
                    <SimpleFormIterator disabled={disabled} disableRemove>
                      <FormDataConsumer>
                        {({ scopedFormData, getSource }) => (
                          <Grid container spacing={1}>
                            {/*
                              name: String! # e.g., "Net 30"
                              code: String # e.g., "Net_30"
                              type: String! # e.g: DUE_DAYS or STANDARD_DUE_DATE
                              dueDays: Int # Required if type = DUE_DAYS
                              dayOfMonthDue: Int # Required if type = STANDARD_DUE_DATE
                              sortKey: Int
                            */}
                            <Grid item xs={12} md={4}>
                              <TextInput
                                source={getSource("code")}
                                label="Term Code"
                                variant="outlined"
                                fullWidth
                                disabled={disabled}
                                validate={validateTermCode}
                                helperText={false}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <TextInput
                                source={getSource("name")}
                                label="Name"
                                variant="outlined"
                                fullWidth
                                disabled={disabled}
                                validate={validateRequired}
                                helperText={false}
                              />
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Box display="flex" gap={1}>
                                <Box flexGrow={1}>
                                  <NumberInput
                                    source={getSource("dueDays")}
                                    label="Due Days"
                                    variant="outlined"
                                    fullWidth
                                    disabled={disabled}
                                    validate={validateDueDays}
                                    helperText={false}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          Days
                                        </InputAdornment>
                                      ),
                                      inputMode: "numeric",
                                      inputProps: {
                                        min: 0,
                                        max: 120,
                                        step: "1",
                                        style: { textAlign: "right" },
                                      },
                                    }}
                                  />
                                </Box>
                                <TermsRemoveButton
                                  form={form}
                                  formData={formData}
                                  scopedFormData={scopedFormData}
                                />
                              </Box>
                            </Grid>
                          </Grid>
                        )}
                      </FormDataConsumer>
                    </SimpleFormIterator>
                  </ArrayInput>
                </Grid>
              </Grid>
            </fieldset>
          </Grid>
        </Grid>
      )}
    </FormDataConsumer>
  );
};

const TermsRemoveButton = ({ form, formData, scopedFormData }) => {
  const notify = useNotify();
  const isCOD =
    scopedFormData?.code === PaymentTermsCodes.COD ||
    scopedFormData?.dueDays === 0;
  const isLast = formData?.paymentTerms?.items?.length === 1;

  const hanldleRemove = async () => {
    const termValues = form.getFieldState("paymentTerms.items")?.value || [];
    const index = termValues.findIndex((f) => f === scopedFormData);

    if (scopedFormData?.id) {
      try {
        const filter = {
          reloadYardId: { eq: window.appUser.reloadYardId },
          paymentTermId: { eq: scopedFormData.id },
        };

        const response = await API.graphql(
          graphqlOperation(searchAccountName, {
            filter,
            limit: 1,
          })
        );
        const total = response?.data?.searchAccounts?.total || 0;

        if (total > 0) {
          const accountName =
            response.data.searchAccounts.items[0].displayName ??
            response.data.searchAccounts.items[0].businessName;
          notify(
            `Selected payment terms is used in ${accountName}.`,
            "warning"
          );
          return;
        }
      } catch (error) {
        logError("reloadYard:InvoicingPanel:hanldleRemove", error, false);
      }
    }

    form.change(
      "paymentTerms.items",
      termValues.filter((_, i) => i !== index)
    );
  };

  return (
    <Button
      label="Remove"
      variant="text"
      color="default"
      size="medium"
      disabled={isCOD || isLast}
      onClick={hanldleRemove}
    >
      <RemoveIcon />
    </Button>
  );
};

export default InvoicingPanel;
